<script>
import { mapGetters } from 'vuex';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { debounce } from '@chatwoot/utils';
import ContactAPI from 'dashboard/api/contacts';
import AgentAPI from 'dashboard/api/agents';
import camelcaseKeys from 'camelcase-keys';

export default {
  components: {
    WootMessageEditor,
  },
  props: {
    modelValue: { type: Object, default: () => ({}) },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      selectedInbox: null,
      subject: '',
      message: '',
      selectedContacts: [],
      selectedSender: null,
      contactSearchQuery: '',
      contacts: [],
      isSearchingContacts: false,
      senderSearchQuery: '',
      agents: [],
      isSearchingAgents: false,
    };
  },
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
      currentAccount: 'getCurrentAccount',
    }),
    contactOptions() {
      return this.contacts.map(contact => ({
        id: contact.id,
        name: contact.name,
        email: contact.email,
        phoneNumber: contact.phoneNumber,
        label: this.getContactLabel(contact),
      }));
    },
    agentOptions() {
      return this.agents.map(agent => ({
        id: agent.id,
        name: agent.name,
        email: agent.email,
        label: agent.email ? `${agent.name} (${agent.email})` : agent.name,
      }));
    },
    // Inboxes email actives (SMTP activé ou OAuth Microsoft/Google)
    emailInboxes() {
      return this.inboxes.filter(inbox => {
        if (inbox.channel_type !== 'Channel::Email') {
          return false;
        }
        // Inclure les inboxes avec SMTP activé
        if (inbox.smtp_enabled) {
          return true;
        }
        // Inclure les inboxes OAuth (Microsoft ou Google) qui peuvent envoyer des emails
        const provider = inbox.provider;
        if (provider === 'microsoft' || provider === 'google') {
          return true;
        }
        // Inclure les inboxes avec IMAP activé (peuvent recevoir, donc probablement actives)
        if (inbox.imap_enabled) {
          return true;
        }
        return false;
      });
    },
    // Inboxes WhatsApp
    whatsappInboxes() {
      return this.inboxes.filter(
        inbox => inbox.channel_type === 'Channel::Whatsapp'
      );
    },
    // Inboxes Telegram
    telegramInboxes() {
      return this.inboxes.filter(
        inbox => inbox.channel_type === 'Channel::Telegram'
      );
    },
    // Inboxes API
    apiInboxes() {
      return this.inboxes.filter(
        inbox => inbox.channel_type === 'Channel::Api'
      );
    },
    // Inboxes Web Widget
    webWidgetInboxes() {
      return this.inboxes.filter(
        inbox => inbox.channel_type === 'Channel::WebWidget'
      );
    },
    // Inboxes SMS
    smsInboxes() {
      return this.inboxes.filter(
        inbox =>
          inbox.channel_type === 'Channel::Sms' ||
          inbox.channel_type === 'Channel::TwilioSms'
      );
    },
    // Inboxes Line
    lineInboxes() {
      return this.inboxes.filter(
        inbox => inbox.channel_type === 'Channel::Line'
      );
    },
    inboxOptions() {
      const options = [];

      // Ajouter les inboxes Email
      if (this.emailInboxes.length > 0) {
        this.emailInboxes.forEach(inbox => {
          options.push({
            id: inbox.id,
            name: `📧 ${inbox.name} (${inbox.email || 'Email'})`,
            type: 'email',
            icon: '📧',
            email: inbox.email,
          });
        });
      }

      // Ajouter les inboxes WhatsApp
      if (this.whatsappInboxes.length > 0) {
        options.push({
          id: 'whatsapp_separator',
          name: '── WhatsApp ──',
          $isDisabled: true,
        });
        this.whatsappInboxes.forEach(inbox => {
          options.push({
            id: inbox.id,
            name: `💬 ${inbox.name} (${inbox.phone_number || 'WhatsApp'})`,
            type: 'whatsapp',
            icon: '💬',
          });
        });
      }

      // Ajouter les inboxes Telegram
      if (this.telegramInboxes.length > 0) {
        options.push({
          id: 'telegram_separator',
          name: '── Telegram ──',
          $isDisabled: true,
        });
        this.telegramInboxes.forEach(inbox => {
          options.push({
            id: inbox.id,
            name: `✈️ ${inbox.name}`,
            type: 'telegram',
            icon: '✈️',
          });
        });
      }

      // Ajouter les inboxes API
      if (this.apiInboxes.length > 0) {
        options.push({
          id: 'api_separator',
          name: '── API ──',
          $isDisabled: true,
        });
        this.apiInboxes.forEach(inbox => {
          options.push({
            id: inbox.id,
            name: `🔌 ${inbox.name}`,
            type: 'api',
            icon: '🔌',
          });
        });
      }

      // Ajouter les inboxes Web Widget
      if (this.webWidgetInboxes.length > 0) {
        options.push({
          id: 'webwidget_separator',
          name: '── Web Widget ──',
          $isDisabled: true,
        });
        this.webWidgetInboxes.forEach(inbox => {
          options.push({
            id: inbox.id,
            name: `🌐 ${inbox.name}`,
            type: 'webwidget',
            icon: '🌐',
          });
        });
      }

      // Ajouter les inboxes SMS
      if (this.smsInboxes.length > 0) {
        options.push({
          id: 'sms_separator',
          name: '── SMS ──',
          $isDisabled: true,
        });
        this.smsInboxes.forEach(inbox => {
          options.push({
            id: inbox.id,
            name: `📱 ${inbox.name} (${inbox.phone_number || 'SMS'})`,
            type: 'sms',
            icon: '📱',
          });
        });
      }

      // Ajouter les inboxes Line
      if (this.lineInboxes.length > 0) {
        options.push({
          id: 'line_separator',
          name: '── Line ──',
          $isDisabled: true,
        });
        this.lineInboxes.forEach(inbox => {
          options.push({
            id: inbox.id,
            name: `💚 ${inbox.name}`,
            type: 'line',
            icon: '💚',
          });
        });
      }

      return options;
    },
    selectedInboxType() {
      if (!this.selectedInbox) return 'email';
      const selected = this.inboxOptions.find(
        opt => opt.id === this.selectedInbox?.id
      );
      return selected?.type || 'email';
    },
    hasEmailInboxes() {
      return this.emailInboxes.length > 0;
    },
    hasAnyInboxes() {
      return this.inboxOptions.length > 0;
    },
    showSubjectField() {
      return this.selectedInboxType === 'email';
    },
    channelTypeLabel() {
      const labels = {
        email: this.$t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_CHANNEL_EMAIL'),
        whatsapp: this.$t(
          'AUTOMATION.ACTION.EMAIL_TO_CONTACT_CHANNEL_WHATSAPP'
        ),
        telegram: this.$t(
          'AUTOMATION.ACTION.EMAIL_TO_CONTACT_CHANNEL_TELEGRAM'
        ),
        api: 'API',
        webwidget: 'Web Widget',
        sms: 'SMS',
        line: 'Line',
      };
      return labels[this.selectedInboxType] || labels.email;
    },
    messageVModel: {
      get() {
        return this.message;
      },
      set(value) {
        this.message = value;
        this.updateValue();
      },
    },
    variablesHint() {
      // Variables disponibles selon le contexte (order, conversation, task)
      return '{{order.order_number}}, {{order.total}}, {{order.status}}, {{conversation.display_id}}, {{conversation.status}}, {{task.title}}, {{task.status}}, {{contact.name}}, {{contact.email}}, {{contact.id}}';
    },
  },
  watch: {
    modelValue: {
      handler(newVal) {
        if (newVal && typeof newVal === 'object') {
          const inboxId = newVal.inbox_id;
          if (inboxId && this.hasAnyInboxes) {
            const found = this.inboxOptions.find(opt => opt.id === inboxId);
            if (found) {
              this.selectedInbox = found;
            } else if (this.inboxOptions.length > 0) {
              // Si l'inbox n'existe plus, sélectionner le premier disponible
              this.selectedInbox = this.inboxOptions[0];
            }
          } else if (
            !this.selectedInbox &&
            this.hasAnyInboxes &&
            this.inboxOptions.length > 0
          ) {
            // Sélectionner le premier inbox par défaut si aucun n'est sélectionné
            this.selectedInbox = this.inboxOptions[0];
          }
          if (newVal.subject !== undefined) {
            this.subject = newVal.subject || '';
          }
          if (newVal.message !== undefined) {
            this.message = newVal.message || '';
          }
          // Mettre à jour les contacts sélectionnés
          if (newVal.contact_ids && Array.isArray(newVal.contact_ids)) {
            Promise.all(
              newVal.contact_ids.map(id =>
                ContactAPI.show(id)
                  .then(res => {
                    const contact = camelcaseKeys(res.data, { deep: true });
                    return {
                      id: contact.id,
                      name: contact.name,
                      email: contact.email,
                      phoneNumber: contact.phoneNumber,
                      label: this.getContactLabel(contact),
                    };
                  })
                  .catch(() => null)
              )
            ).then(contacts => {
              this.selectedContacts = contacts.filter(c => c !== null);
            });
          } else {
            this.selectedContacts = [];
          }
          // Mettre à jour le sender sélectionné
          if (newVal.sender_id) {
            const sender = this.agents.find(a => a.id === newVal.sender_id);
            if (sender) {
              this.selectedSender = {
                id: sender.id,
                name: sender.name,
                email: sender.email,
                label: sender.email
                  ? `${sender.name} (${sender.email})`
                  : sender.name,
              };
            } else {
              this.selectedSender = null;
            }
          } else {
            this.selectedSender = null;
          }
        } else if (
          !this.selectedInbox &&
          this.hasAnyInboxes &&
          this.inboxOptions.length > 0
        ) {
          // Initialiser avec le premier inbox disponible
          this.selectedInbox = this.inboxOptions[0];
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.initializeFromModelValue();
    this.loadAgents();
  },
  methods: {
    initializeFromModelValue() {
      if (this.modelValue && typeof this.modelValue === 'object') {
        const inboxId = this.modelValue.inbox_id;
        if (inboxId && this.hasAnyInboxes) {
          this.selectedInbox = this.inboxOptions.find(
            opt => opt.id === inboxId
          );
          // Si l'inbox sélectionné n'existe plus, sélectionner le premier disponible
          if (!this.selectedInbox && this.inboxOptions.length > 0) {
            this.selectedInbox = this.inboxOptions[0];
          }
        } else if (this.hasAnyInboxes && this.inboxOptions.length > 0) {
          // Sélectionner le premier inbox par défaut si aucun n'est sélectionné
          this.selectedInbox = this.inboxOptions[0];
        }
        this.subject = this.modelValue.subject || '';
        this.message = this.modelValue.message || '';
        // Initialiser les contacts sélectionnés
        if (
          this.modelValue.contact_ids &&
          Array.isArray(this.modelValue.contact_ids)
        ) {
          // Charger les contacts depuis l'API si nécessaire
          Promise.all(
            this.modelValue.contact_ids.map(id =>
              ContactAPI.show(id)
                .then(res => {
                  const contact = camelcaseKeys(res.data, { deep: true });
                  return {
                    id: contact.id,
                    name: contact.name,
                    email: contact.email,
                    phoneNumber: contact.phoneNumber,
                    label: this.getContactLabel(contact),
                  };
                })
                .catch(() => null)
            )
          ).then(contacts => {
            this.selectedContacts = contacts.filter(c => c !== null);
          });
        }
        // Initialiser le sender sélectionné
        if (this.modelValue.sender_id) {
          const sender = this.agents.find(
            a => a.id === this.modelValue.sender_id
          );
          if (sender) {
            this.selectedSender = {
              id: sender.id,
              name: sender.name,
              email: sender.email,
              label: this.getAgentLabel(sender),
            };
          }
        }
        this.updateValue();
      } else if (this.hasAnyInboxes && this.inboxOptions.length > 0) {
        // Initialiser avec le premier inbox disponible
        this.selectedInbox = this.inboxOptions[0];
        this.updateValue();
      }
    },
    updateValue() {
      // Ne pas émettre de valeur si aucun inbox n'est disponible
      if (!this.hasAnyInboxes) {
        this.$emit('update:modelValue', {});
        return;
      }

      const payload = {
        inbox_id: this.selectedInbox?.id || null,
        message: this.message,
      };
      // Ajouter le sujet seulement pour email
      if (this.showSubjectField) {
        payload.subject = this.subject;
      }
      // Ajouter les contacts sélectionnés (ou null pour utiliser le contact de la commande)
      if (this.selectedContacts && this.selectedContacts.length > 0) {
        payload.contact_ids = this.selectedContacts.map(c => c.id);
      }
      // Ajouter le sender sélectionné (ou null pour utiliser l'agent par défaut)
      if (this.selectedSender) {
        payload.sender_id = this.selectedSender.id;
      }
      this.$emit('update:modelValue', payload);
    },
    onInboxChange() {
      // Réinitialiser le sujet si on passe à un canal non-email
      if (!this.showSubjectField) {
        this.subject = '';
      }
      this.updateValue();
    },
    searchContacts: debounce(async function searchContactsFn(query) {
      if (!query || query.length < 2) {
        this.contacts = [];
        return;
      }
      this.isSearchingContacts = true;
      try {
        const response = await ContactAPI.search(query, 1);
        const camelCasedPayload = camelcaseKeys(response.data.payload || [], {
          deep: true,
        });
        this.contacts = (camelCasedPayload || []).filter(
          contact => contact.email || contact.phoneNumber
        );
      } catch (error) {
        // Ignorer les erreurs de recherche
        this.contacts = [];
      } finally {
        this.isSearchingContacts = false;
      }
    }, 300),
    onContactSearch(query) {
      this.contactSearchQuery = query;
      this.searchContacts(query);
    },
    onContactSelect(contacts) {
      this.selectedContacts = contacts || [];
      this.updateValue();
    },
    async loadAgents() {
      // Charger tous les agents du compte
      if (this.currentAccount && this.currentAccount.id) {
        try {
          const response = await AgentAPI.get();
          const camelCasedAgents = camelcaseKeys(response.data || [], {
            deep: true,
          });
          this.agents = camelCasedAgents.filter(agent => agent.confirmed) || [];
        } catch (error) {
          // En cas d'erreur, utiliser une liste vide
          this.agents = [];
        }
      }
    },
    searchAgents: debounce(function searchAgentsFn(query) {
      if (!query) {
        return;
      }
      this.isSearchingAgents = true;
      // Simuler un délai pour l'UX
      setTimeout(() => {
        this.isSearchingAgents = false;
      }, 200);
    }, 300),
    onSenderSelect(sender) {
      this.selectedSender = sender;
      this.updateValue();
    },
    getContactLabel(contact) {
      if (contact.email) {
        return `${contact.name} (${contact.email})`;
      }
      if (contact.phoneNumber) {
        return `${contact.name} (${contact.phoneNumber})`;
      }
      return contact.name;
    },
    getAgentLabel(agent) {
      if (agent.email) {
        return `${agent.name} (${agent.email})`;
      }
      return agent.name;
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col gap-3 mt-3 p-3 bg-n-alpha-1 rounded-lg border border-n-weak"
  >
    <!-- Info sur le canal sélectionné -->
    <div
      v-if="hasAnyInboxes"
      class="flex items-center gap-2 text-xs text-n-slate-11"
    >
      <span class="i-lucide-send-horizontal size-3.5" />
      <span>{{ channelTypeLabel }}</span>
    </div>

    <!-- Sélection de l'inbox -->
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_INBOX_LABEL') }}
      </label>
      <div
        v-if="!hasAnyInboxes"
        class="text-xs text-n-slate-10 bg-n-alpha-2 p-2 rounded border border-n-weak"
      >
        {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_NO_INBOXES') }}
      </div>
      <div v-else class="multiselect-wrap--small">
        <multiselect
          v-model="selectedInbox"
          track-by="id"
          label="name"
          :placeholder="
            $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_INBOX_PLACEHOLDER')
          "
          :options="inboxOptions"
          :allow-empty="false"
          :show-labels="false"
          @update:model-value="onInboxChange"
        />
      </div>
    </div>

    <!-- Sélection des contacts (destinataires) -->
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_CONTACTS_LABEL') }}
      </label>
      <div class="text-xs text-n-slate-10 mb-1">
        {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_CONTACTS_HINT') }}
      </div>
      <div class="multiselect-wrap--small">
        <multiselect
          v-model="selectedContacts"
          :options="contactOptions"
          multiple
          searchable
          :internal-search="false"
          :loading="isSearchingContacts"
          track-by="id"
          label="label"
          :placeholder="
            $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_CONTACTS_PLACEHOLDER')
          "
          allow-empty
          show-labels="false"
          @search-change="onContactSearch"
          @update:model-value="onContactSelect"
        >
          <template #noOptions>
            {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_NO_CONTACTS') }}
          </template>
        </multiselect>
      </div>
    </div>

    <!-- Sélection du sender (expéditeur) -->
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_SENDER_LABEL') }}
      </label>
      <div class="text-xs text-n-slate-10 mb-1">
        {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_SENDER_HINT') }}
      </div>
      <div class="multiselect-wrap--small">
        <multiselect
          v-model="selectedSender"
          :options="agentOptions"
          searchable
          internal-search
          track-by="id"
          label="label"
          :placeholder="
            $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_SENDER_PLACEHOLDER')
          "
          allow-empty
          show-labels="false"
          @update:model-value="onSenderSelect"
        >
          <template #noOptions>
            {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_NO_AGENTS') }}
          </template>
        </multiselect>
      </div>
    </div>

    <!-- Sujet (uniquement pour email) -->
    <div v-if="showSubjectField && hasEmailInboxes">
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_SUBJECT_LABEL') }}
      </label>
      <input
        v-model="subject"
        type="text"
        class="mb-0 w-full"
        :placeholder="
          $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_SUBJECT_PLACEHOLDER')
        "
        @input="updateValue"
      />
    </div>

    <!-- Message avec éditeur riche -->
    <div v-if="hasAnyInboxes">
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_MESSAGE_LABEL') }}
      </label>
      <WootMessageEditor
        v-model="messageVModel"
        rows="4"
        enable-variables
        :placeholder="
          $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_MESSAGE_PLACEHOLDER')
        "
        class="email-message-editor"
      />
    </div>

    <!-- Variables disponibles -->
    <div
      v-if="hasAnyInboxes"
      class="text-xs text-n-slate-10 bg-n-alpha-2 p-2 rounded"
    >
      <div class="font-medium mb-1">
        {{ $t('AUTOMATION.ACTION.EMAIL_TO_CONTACT_VARIABLES_TITLE') }}
      </div>
      <code class="text-[10px] text-n-slate-11">
        {{ variablesHint }}
      </code>
    </div>
  </div>
</template>

<style lang="scss" scoped>
input,
textarea {
  @apply mb-0;
}
.multiselect {
  @apply mb-0;
}
.email-message-editor {
  @apply border border-n-weak rounded;
}
::v-deep .ProseMirror-menubar {
  @apply hidden;
}
</style>
